import { useState, useEffect } from "react";
import { authFetch } from "../api";

// Review-and-apply modal (verification refactor). Opens on "Auto Verify":
//   1. POST /auto-verify/preview  -> current vs RP/SE/AI diff + corrected history/DOM (no writes)
//   2. user picks a source per field (radio)
//   3. POST /auto-verify/apply    -> persists the picked fields + stamps a human verify
// dry_run (default ON) rolls the write back so the flow can be tested against real data.

const LOADING_STAGES = [
  "Fetching RealPlus feed…",
  "Running AI on the description…",
  "Scraping StreetEasy…",
  "Comparing sources…",
];
const SOURCE_LABEL = { rp: "RealPlus", se: "StreetEasy", ai: "AI (description)" };

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

export default function AutoVerifyModal({ listingId, onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [picks, setPicks] = useState({}); // field -> "current" | "rp" | "se" | "ai"
  const [dryRun, setDryRun] = useState(true); // default safe; uncheck to persist
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(
      () => setStage((s) => Math.min(s + 1, LOADING_STAGES.length - 1)),
      1600
    );
    return () => clearInterval(t);
  }, [loading]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStage(0);
    authFetch(`/listings/${listingId}/auto-verify/preview`, { method: "POST" })
      .then((data) => {
        if (cancelled) return;
        if (data?.detail || data?.error) {
          setError(data.detail || data.error);
          setLoading(false);
          return;
        }
        setPreview(data);
        const p = {};
        (data.fields || []).forEach((f) => {
          p[f.field] = f.proposed ? f.proposed.source : "current";
        });
        setPicks(p);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(String(e));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listingId]);

  const setPick = (field, src) => setPicks((p) => ({ ...p, [field]: src }));

  const accepted = {};
  (preview?.fields || []).forEach((f) => {
    const src = picks[f.field];
    if (src && src !== "current" && f.sources[src] !== undefined) {
      accepted[f.field] = f.sources[src];
    }
  });
  const changeCount = Object.keys(accepted).length;

  const apply = async () => {
    setApplying(true);
    setApplyMsg(null);
    try {
      const r = await authFetch(
        `/listings/${listingId}/auto-verify/apply${dryRun ? "?dry_run=true" : ""}`,
        { method: "POST", body: JSON.stringify({ accepted }) }
      );
      if (r?.success) {
        const n = Object.keys(r.applied || {}).length;
        if (dryRun) {
          setApplyMsg(`Dry run OK — ${n} field(s) would change. Nothing saved.`);
        } else {
          setApplyMsg("Applied ✓");
          onApplied?.(r); // parent refresh only on a real write
          setTimeout(() => onClose?.(), 700);
        }
      } else {
        setApplyMsg(`Failed — ${r?.detail || "unknown error"}`);
      }
    } catch (e) {
      setApplyMsg(`Failed — ${e}`);
    } finally {
      setApplying(false);
    }
  };

  const dom = preview?.history?.corrected_dom;
  const curDom = preview?.history?.current_active_dom;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.panel} onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div style={S.header}>
          <div style={{ fontWeight: 600, fontSize: "15px" }}>
            Review &amp; Apply — Listing {listingId}
          </div>
          <button onClick={onClose} style={S.close}>
            &times;
          </button>
        </div>

        {/* body */}
        <div style={S.body}>
          {loading && (
            <div style={S.loadingWrap}>
              <div style={S.spinner} />
              <div style={{ marginTop: "14px", color: "#555", fontSize: "13px" }}>
                {LOADING_STAGES[stage]}
              </div>
              <div style={{ marginTop: "6px", color: "#999", fontSize: "11px" }}>
                Scraping StreetEasy can take up to ~2 minutes.
              </div>
            </div>
          )}

          {!loading && error && (
            <div style={S.error}>Could not build preview — {error}</div>
          )}

          {!loading && preview && (
            <>
              {/* sources */}
              <div style={S.sources}>
                {["rp", "se", "ai"].map((s) => {
                  const st = preview.sources_status?.[s] || "";
                  const ok = st.startsWith("ok");
                  return (
                    <span key={s} style={{ ...S.srcChip, color: ok ? "#2e7d32" : "#c62828" }}>
                      {ok ? "✓" : "✕"} {SOURCE_LABEL[s]}
                    </span>
                  );
                })}
              </div>

              {/* field table */}
              <div style={S.tableHead}>
                <div style={{ flex: "0 0 130px" }}>Field</div>
                <div style={{ flex: 1 }}>Pick the value to apply</div>
              </div>
              {preview.fields.map((f) => {
                const changed = picks[f.field] && picks[f.field] !== "current";
                const options = [
                  { key: "current", label: "Current", val: f.current },
                  ...["rp", "se", "ai"]
                    .filter((s) => f.sources[s] !== undefined)
                    .map((s) => ({ key: s, label: SOURCE_LABEL[s], val: f.sources[s] })),
                ];
                const noSources = options.length === 1; // only "current"
                return (
                  <div
                    key={f.field}
                    style={{ ...S.row, background: changed ? "#fff8e1" : "transparent" }}
                  >
                    <div style={S.rowLabel}>
                      {f.label}
                      {changed && <span style={S.changedDot} title="will change" />}
                    </div>
                    <div style={S.opts}>
                      {noSources && <span style={{ color: "#999", fontSize: "12px" }}>{fmt(f.current)} (no other source)</span>}
                      {!noSources &&
                        options.map((o) => {
                          const sel = picks[f.field] === o.key;
                          const isCurrent = o.key === "current";
                          return (
                            <label
                              key={o.key}
                              style={{
                                ...S.opt,
                                borderColor: sel ? "#1976d2" : "#ddd",
                                background: sel ? "#e3f2fd" : "#fff",
                              }}
                            >
                              <input
                                type="radio"
                                name={`f_${f.field}`}
                                checked={sel}
                                onChange={() => setPick(f.field, o.key)}
                                style={{ marginRight: "5px" }}
                              />
                              <span style={{ color: "#888", fontSize: "10px", marginRight: "4px" }}>
                                {o.label}
                              </span>
                              <b>{fmt(o.val)}</b>
                              {isCurrent && f.flags?.some((x) => x === "current_is_estimated") && (
                                <span style={S.tag}>est.</span>
                              )}
                            </label>
                          );
                        })}
                      {f.flags?.map((fl, i) =>
                        fl && fl.ai_reasoning ? (
                          <div key={i} style={S.reason} title={fl.ai_reasoning}>
                            ⓘ AI: {fl.ai_reasoning.slice(0, 90)}
                            {fl.ai_reasoning.length > 90 ? "…" : ""}
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                );
              })}

              {/* history / DOM */}
              <div style={S.domBox}>
                <div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "6px" }}>
                  History &amp; Days-on-Market{" "}
                  <span style={{ fontWeight: 400, color: "#999" }}>(reference — DOM/history sync is a follow-up)</span>
                </div>
                {dom ? (
                  <div style={{ fontSize: "12px", lineHeight: 1.7 }}>
                    <div>
                      Current stored active DOM: <b>{curDom ?? "—"}</b>
                    </div>
                    <div>
                      SE current cycle: listed <b>{dom.listed_at}</b> → stop{" "}
                      <b>{dom.stop_at || "still on market"}</b>
                      {dom.stop_event ? ` (${dom.stop_event})` : ""}
                    </div>
                    <div>
                      Corrected active DOM:{" "}
                      <b style={{ color: "#1976d2" }}>{dom.active_dom}</b> (calendar{" "}
                      {dom.calendar_days}, off-market {dom.off_market_days})
                    </div>
                    {dom.periods?.map((p, i) => (
                      <div key={i} style={{ color: "#777", marginLeft: "12px" }}>
                        active {p.from} → {p.to} ({p.days}d)
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: "12px", color: "#999" }}>
                    No StreetEasy history available.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* footer */}
        {!loading && preview && (
          <div style={S.footer}>
            <label style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: dryRun ? "#e65100" : "#c62828", cursor: "pointer" }}>
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              Dry run (don't save)
            </label>
            {applyMsg && (
              <span style={{ fontSize: "12px", color: applyMsg.startsWith("Failed") ? "#c62828" : "#2e7d32" }}>
                {applyMsg}
              </span>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={onClose} style={S.cancelBtn}>
              Cancel
            </button>
            <button
              onClick={apply}
              disabled={applying || changeCount === 0}
              style={{
                ...S.applyBtn,
                opacity: applying || changeCount === 0 ? 0.5 : 1,
                cursor: applying || changeCount === 0 ? "default" : "pointer",
                background: dryRun ? "#e65100" : "#1976d2",
              }}
            >
              {applying
                ? "Working…"
                : `${dryRun ? "Dry-run" : "Apply"} ${changeCount} change${changeCount === 1 ? "" : "s"}`}
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes avspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 },
  panel: { background: "#fff", borderRadius: "10px", width: "760px", maxWidth: "94vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 48px rgba(0,0,0,0.3)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #eee" },
  close: { border: "none", background: "none", fontSize: "24px", lineHeight: 1, cursor: "pointer", color: "#888" },
  body: { padding: "14px 18px", overflowY: "auto", flex: 1 },
  loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 0" },
  spinner: { width: "34px", height: "34px", border: "3px solid #e0e0e0", borderTopColor: "#1976d2", borderRadius: "50%", animation: "avspin 0.9s linear infinite" },
  error: { padding: "18px", background: "#fdecea", color: "#c62828", borderRadius: "6px", fontSize: "13px" },
  sources: { display: "flex", gap: "12px", marginBottom: "12px" },
  srcChip: { fontSize: "11px", fontWeight: 600 },
  tableHead: { display: "flex", gap: "10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#999", padding: "0 0 6px", borderBottom: "1px solid #eee" },
  row: { display: "flex", gap: "10px", alignItems: "flex-start", padding: "7px 4px", borderBottom: "1px solid #f2f2f2" },
  rowLabel: { flex: "0 0 130px", fontSize: "12px", fontWeight: 600, color: "#333", display: "flex", alignItems: "center" },
  changedDot: { display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: "#ffb300", marginLeft: "6px" },
  opts: { flex: 1, display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
  opt: { display: "flex", alignItems: "center", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "14px", fontSize: "12px", cursor: "pointer" },
  tag: { marginLeft: "4px", fontSize: "9px", color: "#e65100", border: "1px solid #ffcc80", borderRadius: "6px", padding: "0 3px" },
  reason: { flexBasis: "100%", fontSize: "10px", color: "#888", marginTop: "2px" },
  domBox: { marginTop: "16px", padding: "12px", background: "#f7f9fa", borderRadius: "8px", border: "1px solid #eceff1" },
  footer: { display: "flex", alignItems: "center", gap: "12px", padding: "12px 18px", borderTop: "1px solid #eee" },
  cancelBtn: { padding: "7px 14px", border: "1px solid #ccc", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" },
  applyBtn: { padding: "7px 16px", border: "none", borderRadius: "6px", color: "#fff", fontSize: "13px", fontWeight: 600 },
};
